import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, ChevronDown, ChevronRight, Eye, Lock, ShieldAlert } from "lucide-react";
import Card from "./Card";
import { useApi } from "../hooks/useApi";
import { asSentence, formatExposure, isWithheld } from "../utils/aiFleetPortfolio";

const EMPTY_TEXT = "No AI data leak findings. Axaris shows the verdicts Microsoft Purview DLP and "
  + "Defender for Cloud Apps report about AI apps; it never inspects content.";

const ACTION_PILL = {
  blocked: { label: "Blocked", className: "bg-emerald-100 text-emerald-800" },
  audited: { label: "Audited", className: "bg-amber-100 text-amber-800" },
};

const formatDate = (value) => (value ? new Date(value).toLocaleDateString() : null);

/** The Status column: what Microsoft last said about the alert, or that it was closed. */
export const leakStatusLabel = (finding) => {
  switch (finding.sourceState) {
    case "active":
      return "Active";
    case "notReported": {
      const since = formatDate(finding.notReportedSince);
      return since ? `No longer reported since ${since}` : "No longer reported";
    }
    case "resolved":
      return "Resolved in Microsoft";
    default:
      return "Closed";
  }
};

/** The first two names, then "+N" for the rest. */
export const usersLabel = (users = []) => {
  if (users.length === 0) return "—";
  const shown = users.slice(0, 2).join(", ");
  return users.length > 2 ? `${shown} +${users.length - 2}` : shown;
};

/**
 * The AI Intelligence Data leaks tab: every AI Data Leak Finding in the organization, from
 * `GET /ai-assets/data-leaks` and nothing else. Axaris consumes Microsoft's verdicts (Purview DLP,
 * Defender for Cloud Apps); it never inspects content, and the copy says so.
 *
 * The endpoint serves employee, file and policy names, so it is gated on org-wide view
 * (CanViewAllOrganizationData). A caller with hidden private deals gets the withheld sentence and
 * nothing else. A missing loss figure renders as a dash, never as $0.
 */
export default function AiDataLeaksPanel({ orgId, refreshKey, onOpenAsset }) {
  const apiCall = useApi();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);

  // The only hook-bearing block: every early return below comes after it.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const result = await apiCall("GET", "/ai-assets/data-leaks");
        if (!cancelled) { setData(result); setError(null); }
      } catch (e) {
        if (!cancelled) { setData(null); setError(e?.message || "could not be loaded"); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, refreshKey]);

  if (loading) return null;

  if (isWithheld(data)) {
    return (
      <Card className="p-4 mb-4">
        <div className="flex items-start gap-2 text-sm text-content-secondary">
          <Lock className="w-4 h-4 mt-0.5 shrink-0" />
          <div>
            <div className="font-semibold text-content-primary">AI Data Leak Findings withheld</div>
            <p>{asSentence(data.reason)}</p>
          </div>
        </div>
      </Card>
    );
  }

  if (error || !data) {
    return (
      <Card className="p-4 mb-4">
        <div className="flex items-center gap-2 text-sm text-content-secondary">
          <AlertTriangle className="w-4 h-4 text-amber-600" />
          <span className="font-semibold text-content-primary">AI Data Leak Findings unavailable</span>
          <span className="text-xs">{error ?? "no response"}</span>
        </div>
      </Card>
    );
  }

  const findings = Array.isArray(data.findings) ? data.findings : [];
  if (findings.length === 0) {
    return (
      <Card className="p-4 mb-4">
        <div className="flex items-start gap-2 text-sm text-content-secondary" data-testid="data-leaks-empty">
          <ShieldAlert className="w-4 h-4 mt-0.5 shrink-0" />
          <p>{EMPTY_TEXT}</p>
        </div>
      </Card>
    );
  }

  const summary = data.summary ?? {};
  const tiles = [
    ["Open", summary.open, "summary-open"],
    ["Blocked", summary.blocked, "summary-blocked"],
    ["Audited", summary.audited, "summary-audited"],
    ["Not in inventory", summary.notInInventory, "summary-not-in-inventory"],
    ["No longer reported", summary.notReported, "summary-not-reported"],
    ["Resolved", summary.resolved, "summary-resolved"],
  ];

  return (
    <div>
      <p className="text-xs text-content-secondary mb-3">
        AI Data Leak Findings are the verdicts Microsoft Purview DLP and Defender for Cloud Apps
        report about sensitive data reaching AI apps. Axaris consumes them; it never inspects
        content and does not block anything itself.
      </p>
      <Card className="p-4 mb-4">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {tiles.map(([label, value, testId]) => (
            <div key={label} data-testid={testId}>
              <div className="text-xs text-content-secondary">{label}</div>
              <div className="text-lg font-semibold text-content-primary">{value ?? "—"}</div>
            </div>
          ))}
        </div>
        <p className="text-xs text-content-secondary mt-2">
          Blocked and Audited count open findings; the other figures count every finding, closed ones included.
        </p>
      </Card>
      <Card className="p-0 mb-4 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-content-secondary border-b border-border">
              <th className="px-3 py-2 w-6" aria-label="Expand" />
              <th className="px-3 py-2 font-medium">AI app</th>
              <th className="px-3 py-2 font-medium">Detected by</th>
              <th className="px-3 py-2 font-medium">Action</th>
              <th className="px-3 py-2 font-medium">Users</th>
              <th className="px-3 py-2 font-medium">Information types</th>
              <th className="px-3 py-2 font-medium">Status</th>
              <th className="px-3 py-2 font-medium text-right">Expected annual loss</th>
            </tr>
          </thead>
          <tbody>
            {findings.map((f) => (
              <FindingRow
                key={f.unifiedRiskId}
                finding={f}
                expanded={expandedId === f.unifiedRiskId}
                onToggle={() => setExpandedId(expandedId === f.unifiedRiskId ? null : f.unifiedRiskId)}
                onOpenAsset={onOpenAsset}
                onViewRisk={() => navigate(`/risks?riskId=${f.unifiedRiskId}`)}
              />
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

function FindingRow({ finding: f, expanded, onToggle, onOpenAsset, onViewRisk }) {
  const appName = f.appName || f.aiAssetName || "Unknown AI app";
  const action = ACTION_PILL[f.action];
  const targetDomain = f.targetDomain?.trim();
  return (
    <>
      <tr className="border-b border-border align-top" data-testid="data-leak-row">
        <td className="px-3 py-2">
          <button
            onClick={onToggle}
            aria-label={expanded ? "Hide details" : "Show details"}
            className="text-content-secondary hover:text-content-primary"
          >
            {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </button>
        </td>
        <td className="px-3 py-2">
          {f.inInventory && f.aiAssetId != null ? (
            <button
              onClick={() => onOpenAsset?.(f.aiAssetId)}
              className="text-purple-700 hover:text-purple-900 font-medium text-left"
              title="Open this AI asset in the inventory"
            >
              {appName}
            </button>
          ) : (
            <span className="flex flex-wrap items-center gap-1.5">
              <span className="text-content-primary">{appName}</span>
              <span className="px-1.5 py-0.5 rounded-full text-[11px] font-medium bg-slate-100 text-slate-700">
                Not in inventory
              </span>
            </span>
          )}
          {targetDomain && (
            <div className="text-xs text-content-secondary" data-testid="data-leak-target-domain">
              {targetDomain}
            </div>
          )}
        </td>
        <td className="px-3 py-2 text-content-secondary">{f.detectedBy || "—"}</td>
        <td className="px-3 py-2">
          {action ? (
            <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${action.className}`}>
              {action.label}
            </span>
          ) : (
            <span className="text-content-secondary">—</span>
          )}
        </td>
        <td className="px-3 py-2 text-content-secondary">{usersLabel(f.users)}</td>
        <td className="px-3 py-2 text-content-secondary">
          {(f.informationTypes ?? []).length > 0 ? f.informationTypes.join(", ") : "—"}
        </td>
        <td className="px-3 py-2 text-content-secondary">{leakStatusLabel(f)}</td>
        <td className="px-3 py-2 text-right text-content-primary">{formatExposure(f.expectedAnnualLoss)}</td>
      </tr>
      {expanded && (
        <tr className="border-b border-border bg-theme-hover" data-testid="data-leak-detail">
          <td />
          <td colSpan={7} className="px-3 py-3">
            <dl className="grid grid-cols-1 sm:grid-cols-3 gap-x-4 gap-y-1 text-xs">
              <div>
                <dt className="text-content-secondary">Policy</dt>
                <dd className="text-content-primary">{f.policyName || "—"}</dd>
              </div>
              <div>
                <dt className="text-content-secondary">File</dt>
                <dd className="text-content-primary">{f.fileName || "—"}</dd>
              </div>
              <div>
                <dt className="text-content-secondary">Sensitivity label</dt>
                <dd className="text-content-primary">{f.sensitivityLabel || "—"}</dd>
              </div>
            </dl>
            <button
              onClick={onViewRisk}
              className="mt-3 flex items-center gap-1.5 text-xs px-2.5 py-1 border rounded-lg font-medium transition-all bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-600 hover:text-white hover:border-emerald-600 active:scale-95"
              title="Open in the Risk Register — accept the risk, assign it, or track remediation there"
            >
              <Eye className="w-3 h-3" />
              View risk
            </button>
          </td>
        </tr>
      )}
    </>
  );
}
